import React, { useState, useEffect, useCallback } from 'react';
import { websiteOrderAPI, shippingAPI } from '../services/api';
import DataTable from '../components/ui/DataTable';
import toast from 'react-hot-toast';
import {
  ShoppingBag, X, Package, Truck, CheckCircle2, XCircle,
  Clock, MapPin, Phone, Mail, Eye, RefreshCw, CreditCard, Printer, Send, Navigation, Hourglass,
  MessageCircle, AlertCircle
} from 'lucide-react';

/**
 * The pipeline an order moves through. Cancelling is the only step that goes
 * backwards, and it hands the reserved pieces back to the website.
 */
const STATUSES = [
  // An online payment sits here until the money is confirmed, so the shop's queue
  // never shows an order nobody has paid for
  { key: 'awaiting_payment', label: 'Awaiting Payment', icon: Hourglass, color: '#a16207', bg: '#fefce8' },
  { key: 'placed',    label: 'Placed',    icon: Clock,        color: '#2563eb', bg: '#eff6ff' },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2, color: '#7c3aed', bg: '#f5f3ff' },
  { key: 'packed',    label: 'Packed',    icon: Package,      color: '#c2410c', bg: '#fff7ed' },
  { key: 'shipped',   label: 'Shipped',   icon: Truck,        color: '#0891b2', bg: '#ecfeff' },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle2, color: '#16a34a', bg: '#f0fdf4' },
  { key: 'cancelled', label: 'Cancelled', icon: XCircle,      color: '#dc2626', bg: '#fef2f2' },
];

const statusMeta = (key) => STATUSES.find(s => s.key === key) || STATUSES[0];

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const asDateTime = (v) => v
  ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

export default function WebsiteOrdersPage() {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('');      // '' = every order
  const [counts, setCounts]   = useState({});
  const [detail, setDetail]   = useState(null);    // the order open in the drawer
  const [busy, setBusy]       = useState(false);
  // Null until we know; the drawer's shipping panel stays hidden rather than
  // offering buttons that cannot work
  const [shipping, setShipping] = useState(null);

  useEffect(() => {
    shippingAPI.status()
      .then(res => setShipping(res.data.data))
      .catch(() => setShipping({ connected: false, message: 'Shipping status unavailable' }));
  }, []);

  const load = useCallback(async (which) => {
    const status = which === undefined ? tab : which;
    setLoading(true);
    try {
      const res = await websiteOrderAPI.getAll({ status: status || undefined });
      const rows = res.data.data || [];
      setData(rows);

      // Badges count every order, so they are only recalculated on the full list
      if (!status) {
        const next = { '': rows.length };
        STATUSES.forEach(s => { next[s.key] = rows.filter(r => r.status === s.key).length; });
        setCounts(next);
      }
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(''); }, []);   // eslint-disable-line

  const switchTab = (next) => { setTab(next); load(next); };

  const openDetail = async (row) => {
    try {
      const res = await websiteOrderAPI.getById(row.id);
      setDetail(res.data.data);
    } catch {
      toast.error('Could not open that order');
    }
  };

  const changeStatus = async (order, status) => {
    // Cancelling releases the pieces back onto the website, which is not
    // something to do by a stray click
    if (status === 'cancelled') {
      const reason = window.prompt(
        `Cancel ${order.orderNo}?\n\nThe reserved pieces go back on sale immediately.\n\nReason (optional):`
      );
      if (reason === null) return;
      setBusy(true);
      try {
        await websiteOrderAPI.setStatus(order.id, { status, reason });
        toast.success(`${order.orderNo} cancelled — pieces released`);
        setDetail(null);
        load('');
      } catch (e) {
        toast.error(e.response?.data?.message || 'Could not cancel');
      } finally { setBusy(false); }
      return;
    }

    setBusy(true);
    try {
      await websiteOrderAPI.setStatus(order.id, { status });
      toast.success(`Marked ${statusMeta(status).label.toLowerCase()}`);
      const res = await websiteOrderAPI.getById(order.id);
      setDetail(res.data.data);
      load('');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update');
    } finally { setBusy(false); }
  };

  const runOrderAction = async (action, label) => {
    if (!detail) return;
    setBusy(true);
    try {
      const res = await action(detail.id);
      toast.success(res.data?.message || `${label} done`);
      const fresh = await websiteOrderAPI.getById(detail.id);
      setDetail(fresh.data.data);
      load('');
    } catch (e) {
      toast.error(e.response?.data?.message || `${label} failed`);
    } finally { setBusy(false); }
  };

  const StatusPill = ({ status }) => {
    const m = statusMeta(status);
    const Icon = m.icon;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 20,
        background: m.bg, color: m.color, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap'
      }}>
        <Icon size={11} /> {m.label}
      </span>
    );
  };

  const columns = [
    { header: 'Order', key: 'orderNo', width: 120,
      render: r => <code className="text-mono" style={{ fontSize: 12, fontWeight: 700 }}>{r.orderNo}</code> },
    { header: 'Placed', key: 'placedOn', render: r => <span style={{ fontSize: 12 }}>{asDateTime(r.placedOn)}</span> },
    { header: 'Customer', key: 'customerName', render: r => (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>{r.customerName || '—'}</div>
        <div className="text-muted" style={{ fontSize: 11 }}>{r.phone || r.email}</div>
      </div>
    )},
    { header: 'City', key: 'city', render: r => r.city || <span className="text-muted">—</span> },
    { header: 'Items', key: 'itemCount', align: 'center',
      render: r => <span className="text-mono">{r.itemCount}</span> },
    { header: 'Total', key: 'totalAmount', align: 'right',
      render: r => <span className="text-mono" style={{ fontWeight: 700 }}>{money(r.totalAmount)}</span> },
    { header: 'Payment', key: 'paymentMethod', align: 'center', render: r => (
      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#475569' }}>
        {r.paymentMethod}
      </span>
    )},
    { header: 'Status', key: 'status', align: 'center', render: r => <StatusPill status={r.status} /> },
    { header: '', align: 'center', render: r => (
      <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openDetail(r)} title="Open order">
        <Eye size={14} />
      </button>
    )},
  ];

  const TABS = [{ key: '', label: 'All', count: counts[''] || 0 }]
    .concat(STATUSES.map(s => ({ key: s.key, label: s.label, count: counts[s.key] || 0 })));

  return (
    <div>
      <div className="flex-between mb-6">
        <div>
          <h1>Website Orders</h1>
          <p className="text-muted">
            {data.length} {data.length === 1 ? 'order' : 'orders'}
            {tab ? ` · ${statusMeta(tab).label.toLowerCase()}` : ''}
          </p>
        </div>
        <button className="btn btn-ghost" onClick={() => load()} disabled={loading}>
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(t => {
          const on = tab === t.key;
          return (
            <button
              key={t.key || 'all'}
              type="button"
              onClick={() => switchTab(t.key)}
              className={`btn btn-sm ${on ? 'btn-primary' : 'btn-ghost'}`}
              style={{ display: 'flex', alignItems: 'center', gap: 7 }}
            >
              {t.label}
              <span className="text-mono" style={{
                fontSize: 11, padding: '1px 6px', borderRadius: 10,
                background: on ? 'rgba(255,255,255,0.22)' : 'var(--bg-subtle, #eef2f6)',
                color: on ? '#fff' : 'var(--text-muted)'
              }}>{t.count}</span>
            </button>
          );
        })}
      </div>

      <div
        className="mb-4"
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 14px',
          background: 'var(--bg-subtle, #eff6ff)', border: '1px solid var(--border, #dbeafe)',
          borderRadius: 8, fontSize: 12.5, lineHeight: 1.55
        }}
      >
        <ShoppingBag size={15} style={{ flexShrink: 0, marginTop: 2, color: 'var(--blue, #2563eb)' }} />
        <span className="text-muted">
          A piece is <strong>reserved the moment the order is placed</strong> and disappears from the website,
          so it cannot be sold twice. Cancelling an order puts it back on sale.
          Raise the sales invoice in <strong>Sales</strong> as usual when you dispatch.
        </span>
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ padding: '16px 20px' }}>
            <DataTable
              data={data}
              columns={columns}
              loading={loading}
              searchKeys={['orderNo', 'customerName', 'email', 'phone', 'city']}
              emptyIcon={<ShoppingBag size={36} />}
              emptyText="No orders yet — they appear here the moment somebody buys on the website"
            />
          </div>
        </div>
      </div>

      {detail && (
        <OrderDrawer
          order={detail}
          busy={busy}
          shipping={shipping}
          onClose={() => setDetail(null)}
          onStatus={(s) => changeStatus(detail, s)}
          onCreateShipment={() => runOrderAction(shippingAPI.createShipment, 'Create shipment')}
          onAssignAwb={() => runOrderAction(shippingAPI.assignAwb, 'Book courier')}
          onPickup={() => runOrderAction(shippingAPI.requestPickup, 'Schedule pickup')}
          onTrack={() => runOrderAction(shippingAPI.track, 'Track')}
          onRetryRefund={() => runOrderAction(websiteOrderAPI.retryRefund, 'Refund')}
        />
      )}
    </div>
  );
}

/** The full order — what to pack, where it goes, and what to do next. */
function OrderDrawer({ order, busy, shipping, onClose, onStatus,
                      onCreateShipment, onAssignAwb, onPickup, onTrack, onRetryRefund }) {
  const m = statusMeta(order.status);
  const isCancelled = order.status === 'cancelled';

  // What the shop can move to from here; going back a step is not offered,
  // because an order that has shipped cannot be unpacked
  const currentIndex = STATUSES.findIndex(s => s.key === order.status);
  // An order still waiting for money is not the shop's to move along; it settles
  // itself when Razorpay confirms, or the sweeper releases it
  const awaitingPayment = order.status === 'awaiting_payment';

  const nextSteps = isCancelled
    ? [STATUSES.find(s => s.key === 'placed')]
    : awaitingPayment
      ? []
      : STATUSES.filter((s, i) => i > currentIndex && s.key !== 'cancelled');

  const Row = ({ label, children }) => (
    <div style={{ display: 'flex', gap: 10, padding: '6px 0', fontSize: 12.5 }}>
      <span className="text-muted" style={{ width: 110, flexShrink: 0 }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </div>
  );

  return (
    // The shared overlay centres its child and does not scroll, so anything
    // taller than the screen has its top and bottom put where they cannot be
    // reached. Aligning to the top and letting the overlay scroll means the
    // whole order is always gettable, whatever height it ends up.
    <div
      className="modal-overlay"
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{ alignItems: 'flex-start', overflowY: 'auto', padding: '24px 16px' }}
    >
      {/* The shared .modal keeps overflow visible so dropdowns inside other
          forms can escape it. An order is long enough to run off the screen, so
          this one becomes a column with only its body scrolling — the header and
          the action buttons then stay put instead of being scrolled away. */}
      <div
        className="modal"
        style={{
          maxWidth: 780,
          maxHeight: 'calc(100vh - 48px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <div className="modal-header" style={{ flex: '0 0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h3 style={{ margin: 0 }}>{order.orderNo}</h3>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 20,
              background: m.bg, color: m.color, fontSize: 11, fontWeight: 700
            }}>{m.label}</span>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        <div
          className="modal-body"
          style={{ display: 'grid', gap: 18, overflowY: 'auto', minHeight: 0, flex: '1 1 auto' }}
        >

          {isCancelled && (
            <div style={{ padding: '10px 13px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12.5 }}>
              <strong style={{ color: '#dc2626' }}>Cancelled {asDateTime(order.cancelledOn)}</strong>
              {order.cancelReason ? <div className="text-muted" style={{ marginTop: 3 }}>{order.cancelReason}</div> : null}
              <div className="text-muted" style={{ marginTop: 3 }}>The pieces are back on sale.</div>
            </div>
          )}

          {/* Pieces */}
          <div>
            <h4 style={{ fontSize: 12, fontWeight: 800, margin: '0 0 8px', color: '#0f172a' }}>
              Pieces ({order.items?.length || 0})
            </h4>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
              {(order.items || []).map((it, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                  borderBottom: i < order.items.length - 1 ? '1px solid #f1f5f9' : 'none'
                }}>
                  {it.imageUrl
                    ? <img src={it.imageUrl} alt="" style={{ width: 42, height: 42, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                    : <div style={{ width: 42, height: 42, borderRadius: 6, background: '#f1f5f9', flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{it.itemName}</div>
                    <div className="text-muted" style={{ fontSize: 11 }}>
                      {[it.sku, it.categoryName, it.size, it.weight ? `${it.weight}g` : null]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div className="text-mono" style={{ fontSize: 13, fontWeight: 700 }}>{money(it.amount)}</div>
                    {it.quantity > 1 && <div className="text-muted" style={{ fontSize: 11 }}>× {it.quantity}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            {/* Deliver to */}
            <div>
              <h4 style={{ fontSize: 12, fontWeight: 800, margin: '0 0 8px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                <MapPin size={13} /> Deliver to
              </h4>
              <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>
                <div style={{ fontWeight: 600 }}>{order.shipName}</div>
                <div className="text-muted">
                  {[order.shipLine1, order.shipLine2, order.shipLandmark].filter(Boolean).join(', ')}
                </div>
                <div className="text-muted">
                  {[order.shipCity, order.shipState, order.shipPincode].filter(Boolean).join(', ')}
                </div>
                <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Phone size={12} /> <a href={`tel:${order.phone}`}>{order.phone}</a>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Mail size={12} /> <span style={{ wordBreak: 'break-all' }}>{order.email}</span>
                </div>
              </div>
            </div>

            {/* Money */}
            <div>
              <h4 style={{ fontSize: 12, fontWeight: 800, margin: '0 0 8px', color: '#0f172a' }}>Payment</h4>
              <Row label="Method"><strong style={{ textTransform: 'uppercase' }}>{order.paymentMethod}</strong> · {order.paymentStatus}</Row>
              <Row label="Delivery">{order.deliveryMethod}</Row>
              <Row label="Subtotal"><span className="text-mono">{money(order.subtotal)}</span></Row>
              {order.discountAmount > 0 && <Row label="Discount"><span className="text-mono">− {money(order.discountAmount)}</span></Row>}
              {order.shippingAmount > 0 && <Row label="Shipping"><span className="text-mono">{money(order.shippingAmount)}</span></Row>}
              {order.giftWrapAmount > 0 && <Row label="Gift wrap"><span className="text-mono">{money(order.giftWrapAmount)}</span></Row>}
              <Row label="GST (incl.)"><span className="text-mono">{money(order.cgstAmount + order.sgstAmount)}</span></Row>
              <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 6, paddingTop: 6 }}>
                <Row label="Total"><span className="text-mono" style={{ fontSize: 15, fontWeight: 800 }}>{money(order.totalAmount)}</span></Row>
              </div>
              {order.gstin && <Row label="GSTIN">{order.gstin}</Row>}
              {order.razorpayPaymentId && (
                <Row label="Razorpay"><code className="text-mono" style={{ fontSize: 11 }}>{order.razorpayPaymentId}</code></Row>
              )}
              {order.refundId && (
                <Row label="Refunded"><span className="text-mono">{money(order.refundAmount)}</span></Row>
              )}

              {/* Money owed but not yet sent. A refund can fail for reasons that
                  pass on their own — Razorpay briefly unreachable, a payment not
                  yet settled — so this is worth trying again rather than being a
                  dead end that leaves the customer out of pocket and nobody told. */}
              {!order.refundId && order.razorpayPaymentId &&
               (order.paymentStatus === 'refund_failed' ||
                (order.status === 'cancelled' && order.paymentStatus === 'paid')) && (
                <div style={{
                  marginTop: 10, padding: '10px 12px', borderRadius: 8,
                  background: '#fef2f2', border: '1px solid #fecaca',
                  display: 'grid', gap: 8,
                }}>
                  <div style={{ display: 'flex', gap: 7, fontSize: 12.5, color: '#991b1b', lineHeight: 1.55 }}>
                    <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                      <strong>{money(order.totalAmount)}</strong> has not gone back to the customer yet.
                    </span>
                  </div>
                  <div>
                    <button className="btn btn-sm btn-danger" disabled={busy} onClick={onRetryRefund}>
                      <RefreshCw size={14} /> Refund now
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Parcel — only when Shiprocket is actually connected */}
          {shipping?.connected && order.status !== 'cancelled' && (
            <div>
              <h4 style={{ fontSize: 12, fontWeight: 800, margin: '0 0 8px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Truck size={13} /> Parcel
              </h4>

              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, background: '#f8fafc', display: 'grid', gap: 10 }}>
                {order.awbCode ? (
                  <div style={{ fontSize: 12.5, lineHeight: 1.7 }}>
                    <div><strong>{order.courierName || 'Courier'}</strong> · <code className="text-mono">{order.awbCode}</code></div>
                    {order.shippingStatus && <div className="text-muted">Last seen: {order.shippingStatus}</div>}
                  </div>
                ) : (
                  <div className="text-muted" style={{ fontSize: 12.5 }}>
                    {order.shipmentId
                      ? 'Filed with Shiprocket. Book a courier to get the tracking number and label.'
                      : 'Not sent to Shiprocket yet.'}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {!order.shipmentId && (
                    <button className="btn btn-sm btn-ghost" disabled={busy} onClick={onCreateShipment}>
                      <Package size={14} /> Create shipment
                    </button>
                  )}
                  {order.shipmentId && !order.awbCode && (
                    <button className="btn btn-sm btn-primary" disabled={busy} onClick={onAssignAwb}>
                      <Truck size={14} /> Book courier
                    </button>
                  )}
                  {order.labelUrl && (
                    <a className="btn btn-sm btn-ghost" href={order.labelUrl} target="_blank" rel="noopener noreferrer">
                      <Printer size={14} /> Print label
                    </a>
                  )}
                  {order.awbCode && (
                    <>
                      <button className="btn btn-sm btn-ghost" disabled={busy} onClick={onPickup}>
                        <Send size={14} /> Schedule pickup
                      </button>
                      <button className="btn btn-sm btn-ghost" disabled={busy} onClick={onTrack}>
                        <Navigation size={14} /> Track
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {shipping && !shipping.connected && (
            <div style={{ padding: '10px 13px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, fontSize: 12.5 }}>
              <span className="text-muted">{shipping.message}</span>
            </div>
          )}

          {/* What the customer was actually told. A shop that cannot see this ends
              up telephoning to ask whether the tracking number reached them. */}
          {order.notifications && order.notifications.length > 0 && (
            <div>
              <h4 style={{ fontSize: 12, fontWeight: 800, margin: '0 0 8px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                <MessageCircle size={13} /> WhatsApp
              </h4>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
                {order.notifications.map((n, i) => {
                  const ok = n.status === 'sent';
                  const skipped = n.status === 'skipped';
                  return (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 12px',
                      borderBottom: i < order.notifications.length - 1 ? '1px solid #f1f5f9' : 'none',
                      background: ok ? '#f0fdf4' : skipped ? '#f8fafc' : '#fef2f2'
                    }}>
                      {ok
                        ? <CheckCircle2 size={13} style={{ color: '#16a34a', flexShrink: 0, marginTop: 2 }} />
                        : <AlertCircle size={13} style={{ color: skipped ? '#94a3b8' : '#dc2626', flexShrink: 0, marginTop: 2 }} />}
                      <div style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>
                          {n.event.replace(/_/g, ' ')}
                          <span className="text-muted" style={{ fontWeight: 600, marginLeft: 6 }}>
                            {ok ? asDateTime(n.sentOn) : n.status}
                          </span>
                        </div>
                        {n.bodyPreview && <div className="text-muted" style={{ marginTop: 2 }}>{n.bodyPreview}</div>}
                        {n.error && <div style={{ marginTop: 2, color: skipped ? '#64748b' : '#dc2626' }}>{n.error}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(order.giftMessage || order.notes) && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12, fontSize: 12.5 }}>
              {order.giftWrap && <div><strong>Gift wrapped.</strong> {order.giftMessage}</div>}
              {order.notes && <div style={{ marginTop: order.giftWrap ? 6 : 0 }}><strong>Note:</strong> {order.notes}</div>}
            </div>
          )}

          <div className="text-muted" style={{ fontSize: 11.5 }}>
            Placed {asDateTime(order.placedOn)}
            {order.customerCode ? ` · Customer ${order.customerCode}` : ''}
          </div>
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, flex: '0 0 auto' }}>
          <div>
            {!isCancelled && (
              <button
                className="btn btn-ghost"
                disabled={busy}
                onClick={() => onStatus('cancelled')}
                style={{ color: 'var(--red)' }}
              >
                <XCircle size={15} /> Cancel order
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {nextSteps.map(s => {
              const Icon = s.icon;
              return (
                <button
                  key={s.key}
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => onStatus(s.key)}
                >
                  <Icon size={15} /> Mark {s.label}
                </button>
              );
            })}
            {nextSteps.length === 0 && (
              <span className="text-muted" style={{ fontSize: 12.5, alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 6 }}>
                {awaitingPayment
                  ? <><CreditCard size={14} /> Waiting for the customer to pay. The pieces stay reserved until then.</>
                  : 'Nothing left to do on this order.'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
